import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { api, setToken, getToken, formatApiError } from '@/lib/api';

const AuthContext = createContext(null);

/**
 * AuthProvider — status:
 *   'loading' → verifying persisted token
 *   'authed'  → user object available
 *   'guest'   → no valid session
 */
export function AuthProvider({ children }) {
  const [status, setStatus] = useState('loading');
  const [user, setUser] = useState(null);

  const refresh = useCallback(async () => {
    const t = getToken();
    if (!t) {
      setUser(null);
      setStatus('guest');
      return;
    }
    try {
      const { data } = await api.get('/auth/me');
      setUser(data);
      setStatus('authed');
    } catch {
      setToken(null);
      setUser(null);
      setStatus('guest');
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = useCallback(async (email, password) => {
    try {
      const { data } = await api.post('/auth/login', { email, password });
      setToken(data.access_token);
      setUser(data.user);
      setStatus('authed');
      return { ok: true };
    } catch (err) {
      return { ok: false, error: formatApiError(err) };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } catch {
      /* ignore — token already invalid */
    }
    setToken(null);
    setUser(null);
    setStatus('guest');
  }, []);

  return (
    <AuthContext.Provider value={{ status, user, login, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
